import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const FinancingLifecycle = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [financing, setFinancing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authorized
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    if (!isLoggedIn) {
      navigate('/signin');
    }
  }, [navigate]);

  // Mock data - in production, fetch from API
  useEffect(() => {
    setTimeout(() => {
      const mockData = {
        1: {
          id: 1,
          businessName: 'Tech Startup LLC',
          contactName: 'Ahmed Khan',
          email: 'ahmed@techstartup.com',
          phone: '+1-555-0123',
          financingAmount: '$250,000',
          financingType: 'Murabaha',
          status: 'Pending Review',
          submittedDate: '2024-01-15',
          priority: 'High',
          businessType: 'Technology Startup',
          purpose: 'Equipment Purchase & Working Capital',
          timeframe: '2-3 months',
          revenue: '$500,000',
          profit: '$75,000',
          collateral: 'Equipment & Real Estate',
          timeline: [
            { stage: 'Application Submitted', status: 'completed', date: '2024-01-15', description: 'Initial application received' },
            { stage: 'Document Review', status: 'in-progress', date: '2024-01-16', description: 'Reviewing submitted documents' },
            { stage: 'Financial Analysis', status: 'pending', date: null, description: 'Analyzing financials and business viability' },
            { stage: 'Shariah Compliance Review', status: 'pending', date: null, description: 'Ensuring all aspects meet Shariah requirements' },
            { stage: 'Committee Approval', status: 'pending', date: null, description: 'Final approval by financing committee' },
            { stage: 'Contract Preparation', status: 'pending', date: null, description: 'Drafting Shariah-compliant financing agreement' },
            { stage: 'Financing Disbursed', status: 'pending', date: null, description: 'Funds transferred to business' }
          ],
          documents: [
            { name: 'Business Plan', status: 'Received', uploadedDate: '2024-01-15' },
            { name: 'Financial Statements', status: 'Received', uploadedDate: '2024-01-15' },
            { name: 'Tax Returns', status: 'Pending', uploadedDate: null },
            { name: 'Business License', status: 'Received', uploadedDate: '2024-01-15' },
            { name: 'Collateral Documentation', status: 'Under Review', uploadedDate: '2024-01-16' }
          ],
          notes: [
            { date: '2024-01-16', author: 'Sarah Admin', note: 'Initial review looks promising. Following up on tax returns.' },
            { date: '2024-01-15', author: 'System', note: 'Application submitted successfully.' }
          ]
        },
        2: {
          id: 2,
          businessName: 'Green Energy Solutions',
          contactName: 'Fatima Ali',
          email: 'fatima@greenenergy.com',
          phone: '+1-555-0124',
          financingAmount: '$500,000',
          financingType: 'Musharaka',
          status: 'Under Review',
          submittedDate: '2024-01-14',
          priority: 'High',
          businessType: 'Renewable Energy',
          purpose: 'Solar Panel Manufacturing Equipment',
          timeframe: '3-4 months',
          revenue: '$1,200,000',
          profit: '$180,000',
          collateral: 'Equipment & Inventory',
          timeline: [
            { stage: 'Application Submitted', status: 'completed', date: '2024-01-14', description: 'Initial application received' },
            { stage: 'Document Review', status: 'completed', date: '2024-01-16', description: 'All documents verified' },
            { stage: 'Financial Analysis', status: 'in-progress', date: '2024-01-18', description: 'Analyzing financials and business viability' },
            { stage: 'Shariah Compliance Review', status: 'pending', date: null, description: 'Ensuring all aspects meet Shariah requirements' },
            { stage: 'Committee Approval', status: 'pending', date: null, description: 'Final approval by financing committee' },
            { stage: 'Contract Preparation', status: 'pending', date: null, description: 'Drafting Shariah-compliant financing agreement' },
            { stage: 'Financing Disbursed', status: 'pending', date: null, description: 'Funds transferred to business' }
          ],
          documents: [
            { name: 'Business Plan', status: 'Received', uploadedDate: '2024-01-14' },
            { name: 'Financial Statements (3 years)', status: 'Received', uploadedDate: '2024-01-14' },
            { name: 'Tax Returns', status: 'Received', uploadedDate: '2024-01-14' },
            { name: 'Business License', status: 'Received', uploadedDate: '2024-01-14' },
            { name: 'Equipment Valuation Report', status: 'Received', uploadedDate: '2024-01-16' },
            { name: 'Environmental Compliance Certificate', status: 'Received', uploadedDate: '2024-01-16' }
          ],
          notes: [
            { date: '2024-01-18', author: 'Financial Analyst', note: 'Strong financials. Revenue growth of 45% YoY. Recommended for approval.' },
            { date: '2024-01-16', author: 'Document Reviewer', note: 'All required documents received and verified.' },
            { date: '2024-01-14', author: 'System', note: 'Application submitted successfully.' }
          ]
        },
        4: {
          id: 4,
          businessName: 'Islamic Education Center',
          contactName: 'Aisha Ibrahim',
          email: 'aisha@ieducation.org',
          phone: '+1-555-0126',
          financingAmount: '$75,000',
          financingType: 'Qard Hassan',
          status: 'Pending Documents',
          submittedDate: '2024-01-12',
          priority: 'Low',
          businessType: 'Non-Profit Education',
          purpose: 'Building Expansion & Library',
          timeframe: '1-2 months',
          revenue: '$200,000',
          profit: 'N/A (Non-Profit)',
          collateral: 'Building & Land',
          timeline: [
            { stage: 'Application Submitted', status: 'completed', date: '2024-01-12', description: 'Initial application received' },
            { stage: 'Document Review', status: 'in-progress', date: '2024-01-13', description: 'Waiting for additional documents' },
            { stage: 'Financial Analysis', status: 'pending', date: null, description: 'Analyzing financials and business viability' },
            { stage: 'Shariah Compliance Review', status: 'pending', date: null, description: 'Ensuring all aspects meet Shariah requirements' },
            { stage: 'Committee Approval', status: 'pending', date: null, description: 'Final approval by financing committee' },
            { stage: 'Contract Preparation', status: 'pending', date: null, description: 'Drafting Shariah-compliant financing agreement' },
            { stage: 'Financing Disbursed', status: 'pending', date: null, description: 'Funds transferred to business' }
          ],
          documents: [
            { name: 'Non-Profit Charter', status: 'Received', uploadedDate: '2024-01-12' },
            { name: 'Financial Statements', status: 'Received', uploadedDate: '2024-01-12' },
            { name: 'Tax Exemption Certificate', status: 'Pending', uploadedDate: null },
            { name: 'Board Meeting Minutes', status: 'Pending', uploadedDate: null },
            { name: 'Building Plans', status: 'Received', uploadedDate: '2024-01-12' }
          ],
          notes: [
            { date: '2024-01-13', author: 'Document Reviewer', note: 'Waiting for tax exemption certificate and board meeting minutes.' },
            { date: '2024-01-12', author: 'System', note: 'Application submitted successfully.' }
          ]
        }
      };

      setFinancing(mockData[id] || null);
      setIsLoading(false);
    }, 500);
  }, [id]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-gray-600">Loading financing details...</p>
        </div>
      </div>
    );
  }

  if (!financing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Financing application not found</p>
          <Button onClick={() => navigate('/crm/admin')} className="mt-4">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const getStageIcon = (status) => {
    if (status === 'completed') {
      return (
        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    } else if (status === 'in-progress') {
      return (
        <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
          <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    }
  };

  const getDocumentStatusColor = (status) => {
    switch (status) {
      case 'Received':
        return 'bg-green-100 text-green-800';
      case 'Under Review':
        return 'bg-blue-100 text-blue-800';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/crm/admin')}
              className="mr-4"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </Button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Financing Application Lifecycle</h1>
              <p className="text-sm text-gray-600">{financing.businessName} - {financing.financingType}</p>
            </div>
            <div>
              <Badge className="bg-yellow-100 text-yellow-800 text-sm px-3 py-1">
                {financing.status}
              </Badge>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Timeline */}
          <div className="lg:col-span-2">
            <Card className="mb-6">
              <CardHeader>
                <h2 className="text-lg font-semibold">Application Timeline</h2>
              </CardHeader>
              <CardContent>
                <div className="space-y-8">
                  {financing.timeline.map((stage, index) => (
                    <div key={index} className="relative flex items-start">
                      {index < financing.timeline.length - 1 && (
                        <div className="absolute left-5 top-10 bottom-0 w-0.5 bg-gray-300" style={{ marginLeft: '-0.125rem' }}></div>
                      )}
                      <div className="flex-shrink-0">
                        {getStageIcon(stage.status)}
                      </div>
                      <div className="ml-4 flex-1">
                        <h3 className="text-lg font-medium text-gray-900">{stage.stage}</h3>
                        <p className="text-sm text-gray-600 mt-1">{stage.description}</p>
                        {stage.date && (
                          <p className="text-xs text-gray-500 mt-2">
                            {stage.status === 'completed' ? 'Completed' : 'Started'}: {stage.date}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Documents */}
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold">Required Documents</h2>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {financing.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-6 h-6 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div>
                          <p className="font-medium text-gray-900">{doc.name}</p>
                          {doc.uploadedDate && (
                            <p className="text-xs text-gray-500">Uploaded: {doc.uploadedDate}</p>
                          )}
                        </div>
                      </div>
                      <Badge className={getDocumentStatusColor(doc.status)}>
                        {doc.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-1">
            {/* Business Details */}
            <Card className="mb-6">
              <CardHeader>
                <h2 className="text-lg font-semibold">Business Details</h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Business Name</p>
                  <p className="font-medium text-gray-900">{financing.businessName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Contact Person</p>
                  <p className="font-medium text-gray-900">{financing.contactName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium text-gray-900">{financing.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium text-gray-900">{financing.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Business Type</p>
                  <p className="font-medium text-gray-900">{financing.businessType}</p>
                </div>
              </CardContent>
            </Card>

            {/* Financing Details */}
            <Card className="mb-6">
              <CardHeader>
                <h2 className="text-lg font-semibold">Financing Details</h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Financing Amount</p>
                  <p className="text-2xl font-bold text-gray-900">{financing.financingAmount}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Financing Type</p>
                  <p className="font-medium text-gray-900">{financing.financingType}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Purpose</p>
                  <p className="font-medium text-gray-900">{financing.purpose}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Expected Timeframe</p>
                  <p className="font-medium text-gray-900">{financing.timeframe}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Priority</p>
                  <Badge className={
                    financing.priority === 'High' ? 'bg-red-100 text-red-800' :
                    financing.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }>
                    {financing.priority}
                  </Badge>
                </div>
              </CardContent>
            </Card>

            {/* Financial Information */}
            <Card className="mb-6">
              <CardHeader>
                <h2 className="text-lg font-semibold">Financial Information</h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Annual Revenue</p>
                  <p className="font-medium text-gray-900">{financing.revenue}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Annual Profit</p>
                  <p className="font-medium text-gray-900">{financing.profit}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Collateral</p>
                  <p className="font-medium text-gray-900">{financing.collateral}</p>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold">Internal Notes</h2>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {financing.notes.map((note, index) => (
                    <div key={index} className="border-l-4 border-blue-500 pl-4 py-2">
                      <p className="text-sm text-gray-900">{note.note}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {note.author} - {note.date}
                      </p>
                    </div>
                  ))}
                </div>
                <Button className="w-full mt-4" variant="outline">
                  Add Note
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancingLifecycle;

